import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTableColumnHeader,
  type DataTableFilter,
  InformationCard,
  InformationTable,
  type InformationTableEntity,
  type EntityValue,
  dateRangeFilterFn,
  facetedFilterFn,
} from '@folio/components';
import { MAX_FLEET_SIZE } from '@folio/constants';
import { formatDateWithTime, useAssertedParams } from '@folio/lib';
import type { ColumnDef } from '@tanstack/react-table';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import {
  useHasPolicyProductConfig,
  usePolicyProductConfig,
} from '@folio/utils/usePolicyProductConfig';
import {
  type Asset,
  type AssetDto,
  keyValueFieldsToObject,
  toKeyValueFields,
  useAssetControllerEditAsset,
  useBranchControllerForkBranch,
  useViewSpecificControllerListPolicyAssets,
} from 'korint-sdk';
import { SquarePen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { perilLabel, tierLabel } from '../shared-quote/labels';
import { AssetCardStatus, getAssetStatus } from './AssetCardStatus';
import { Group } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

type AssetInformationCardProps = {
  allowEdition?: boolean;
};

export function AssetInformationCardModule({ allowEdition = true }: AssetInformationCardProps) {
  const { t } = useTranslation();
  const { branchId } = useAssertedParams();
  const { policy } = usePolicyOnDashboard();
  const navigate = useNavigate();

  const {
    data: assets,
    refetch,
    error: assetsError,
  } = useViewSpecificControllerListPolicyAssets(policy.id ?? '', {
    count: MAX_FLEET_SIZE,
    ...(branchId ? { branchId } : {}),
  });

  const customerId = policy?.customers?.[0];
  const { mutateAsync: editAsset } = useAssetControllerEditAsset();
  const { mutateAsync: forkBranch } = useBranchControllerForkBranch();

  const hasInformationCardConfig = useHasPolicyProductConfig('assetInformations');
  if (!hasInformationCardConfig) {
    return null;
  }

  const assetInformationConfig = usePolicyProductConfig('assetInformations');
  const productCustomPath = usePolicyProductConfig('productCustomPath');

  const resolvedTitle = t(`${policy.productId}.assetInformations.title`);

  async function redirectToForm() {
    if (!policy.id || !customerId) {
      console.warn('Tried to edit asset information card but policy.id or customer is not defined');
      return;
    }

    if (!branchId) {
      navigate(`/mta${productCustomPath}/${customerId}/${policy.id}/new`);
      return;
    }

    const branch = await forkBranch({
      policyId: policy.id ?? '',
      branchId,
      data: {
        branchType: 'NEW_BUSINESS',
      },
    });
    navigate(`/discovery${productCustomPath}/${customerId}/${policy.id}/${branch.id}`);
  }

  function mapAsset(asset: AssetDto): Asset {
    return {
      ...asset,
      customFields: keyValueFieldsToObject(asset.customFields ?? []),
    };
  }

  if (assetsError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{resolvedTitle}</CardTitle>
        </CardHeader>
        <CardContent>{t('assetInformations.loadError')}</CardContent>
      </Card>
    );
  }

  if (!assets?.data || assets.data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{resolvedTitle}</CardTitle>
        </CardHeader>
        <CardContent>{t('assetInformations.noAssets')}</CardContent>
      </Card>
    );
  }

  if (assetInformationConfig.display === 'table') {
    const mappedAssets = assets.data.map(mapAsset);

    const dateColumn = (id: 'startedAt' | 'stoppedAt'): ColumnDef<InformationTableEntity> => ({
      id,
      accessorFn: row => (row as Asset)[id] ?? '',
      filterFn: dateRangeFilterFn,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t(`assetTable.${id}`)} />
      ),
      cell: ({ getValue }) => {
        const value = getValue<string>();
        return value ? (
          formatDateWithTime(new Date(value))
        ) : (
          <Text inline tone="muted">
            —
          </Text>
        );
      },
    });

    const tierOf = (row: InformationTableEntity) => (row as Asset).quote?.options?.tier ?? '';
    const perilsOf = (row: InformationTableEntity) =>
      ((row as Asset).quote?.options?.perils ?? []).map(({ id }) =>
        perilLabel(t, policy.productId, id),
      );

    // Per-vehicle coverage only exists on fleets; single-asset products show it on the policy card.
    const hasPerAssetCoverage = mappedAssets.some(asset => asset.quote?.options?.tier);

    const coverageColumns: ColumnDef<InformationTableEntity>[] = hasPerAssetCoverage
      ? [
          {
            id: 'tier',
            accessorFn: tierOf,
            filterFn: facetedFilterFn,
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title={t('assetTable.tier')} />
            ),
            cell: ({ getValue }) => {
              const tier = getValue<string>();
              return tier ? (
                tierLabel(t, policy.productId, tier)
              ) : (
                <Text inline tone="muted">
                  —
                </Text>
              );
            },
          },
          {
            id: 'perils',
            accessorFn: row => perilsOf(row).join(', '),
            filterFn: facetedFilterFn,
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title={t('assetTable.perils')} />
            ),
            cell: ({ getValue }) => {
              const perils = getValue<string>();
              return perils ? (
                perils
              ) : (
                <Text inline tone="muted">
                  —
                </Text>
              );
            },
          },
        ]
      : [];

    const extraColumns: ColumnDef<InformationTableEntity>[] = [
      {
        id: 'status',
        accessorFn: row => getAssetStatus(row as Asset, policy),
        filterFn: facetedFilterFn,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('assetTable.status')} />
        ),
        cell: ({ row }) => (
          <AssetCardStatus asset={row.original as Asset} policy={policy} showDates={false} />
        ),
      },
      dateColumn('startedAt'),
      dateColumn('stoppedAt'),
      ...coverageColumns,
    ];

    const statusOptions = [
      ...new Set(mappedAssets.map(asset => getAssetStatus(asset, policy))),
    ].map(status => ({ label: t(`assetCardStatus.${status}`), value: status }));

    const filters: DataTableFilter[] = [
      ...(hasPerAssetCoverage
        ? [{ columnId: 'tier', title: t('assetTable.tier'), type: 'faceted' as const }]
        : []),
      {
        columnId: 'status',
        title: t('assetTable.status'),
        type: 'faceted',
        options: statusOptions,
      },
      { columnId: 'startedAt', title: t('assetTable.startedAt'), type: 'date' },
      { columnId: 'stoppedAt', title: t('assetTable.stoppedAt'), type: 'date' },
    ];

    return (
      <InformationTable
        title={resolvedTitle}
        productId={policy.productId}
        entityType="asset"
        fieldsConfig={assetInformationConfig.fieldsConfig}
        entities={mappedAssets}
        extraColumns={extraColumns}
        filters={filters}
        headerAction={
          allowEdition ? (
            <Button type="button" variant="ghost" size="sm" onClick={redirectToForm}>
              {t('informationField.editContract')}
              <SquarePen className="ml-2 size-4" />
            </Button>
          ) : undefined
        }
      />
    );
  }

  return assets.data.map(asset => (
    <InformationCard
      key={asset.id}
      productId={policy.productId}
      entityType="asset"
      entity={mapAsset(asset)}
      updateEntity={async (key: string, value: EntityValue) => {
        await editAsset({
          assetId: asset.id,
          params: branchId ? { branchId } : undefined,
          data: {
            customFields: toKeyValueFields({ [key]: value }),
          },
        });
        refetch();
      }}
      title={
        <Group gap={2}>
          {resolvedTitle} {asset.name}
          <AssetCardStatus asset={mapAsset(asset)} policy={policy} />
        </Group>
      }
      fieldsConfig={assetInformationConfig.fieldsConfig}
      redirectToForm={redirectToForm}
      allowEdition={allowEdition}
    />
  ));
}
